import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useLiquidEffect } from '@/hooks/useLiquidEffect';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  liquidEffect?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-wine-burgundy text-white hover:bg-wine-merlot shadow-wine',
  secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
  danger: 'bg-wine-burgundy text-white hover:bg-wine-merlot shadow-wine',
  outline: 'border-2 border-wine-burgundy text-wine-burgundy hover:bg-wine-burgundy hover:text-white transition-colors',
  ghost: 'text-wine-burgundy hover:bg-wine-burgundy/10',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  fullWidth = false,
  icon,
  disabled,
  className = '',
  liquidEffect = false,
  onClick,
  ...props
}) => {
  const baseStyles = 'font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2';
  const widthStyle = fullWidth ? 'w-full' : '';
  const { elementRef, triggerLiquid } = useLiquidEffect();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (liquidEffect && !disabled && !loading) {
      triggerLiquid(e);
    }
    onClick?.(e);
  };

  return (
    <motion.button
      ref={elementRef}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${className} ${liquidEffect ? 'relative overflow-hidden' : ''}`}
      disabled={disabled || loading}
      onClick={handleClick}
      {...(props as any)}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Caricamento...
        </>
      ) : (
        <>
          {icon && <span>{icon}</span>}
          {children}
        </>
      )}
    </motion.button>
  );
};
